import React, {
  useRef,
  forwardRef,
  useEffect,
  useState,
  Suspense,
} from "react";
import { Canvas } from "@react-three/fiber";
import { Euler, Group, MathUtils, Quaternion } from "three";
import { throttle } from "lodash";
import styled, { css, keyframes } from "styled-components";
import { BowlingLayoutDualAngle } from "./BowlingLayoutDualAngle";
import { BowlingLayoutVLS } from "./BowlingLayoutVLS";
import {
  sanitizeSpanMeasurements,
  parseInchFraction,
  isValidSpanMeasurements,
} from "./utils/fingerLayoutUtils";
import { useTranslation } from "react-i18next";
import { BowlingLayout2LS } from "./BowlingLayout2LS";
import { BowlingLayoutPAL } from "./BowlingLayoutPAL";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 0, 0, 0.6);
  padding: 6px 12px;
  border-radius: 16px;
  color: white;
  font-size: 12px;
  z-index: 10;
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ErrorOverlay = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  z-index: 10;
`;

const ErrorMessage = styled.div`
  background: rgba(180, 0, 0, 0.8);
  padding: 6px 12px;
  border-radius: 8px;
  color: white;
  font-size: 11px;
  text-align: center;
`;

const BallView = styled.div`
  display: flex;
  flex: 3;
  height: ${(props) => (props.$isPhone ? "240px" : "480px")};
  width: ${(props) => (props.$isPhone ? "360px" : "480px")};
`;

const BallGLView = forwardRef(
  (
    {
      layoutType,
      pinToPapDistance,
      pinAngle,
      valAngle,
      pinBuffer,
      pinToCenter,
      pinAboveLine,
      mbToPapDistance,
      playerPapOffset,
      spanMeasurements,
      spanMeasurement,
      isPhone,
      access_token,
      ballId,
    },
    ref,
  ) => {
    const ballRef = useRef(null);
    const lastTouch = useRef({ x: null, y: null });
    const { t } = useTranslation();
    const [isTextureLoading, setIsTextureLoading] = useState(false);
    const [hasTextureError, setHasTextureError] = useState(false);
    const [hasLayoutError, setHasLayoutError] = useState(false);

    const handleTextureStatusChange = (status) => {
      setIsTextureLoading(status.isLoading);
      setHasTextureError(status.hasError);
    };

    const handleLayoutError = (hasError) => {
      setHasLayoutError(hasError);
    };
    // Unifica eventos de mouse y touch
    const isTouchEvent = (event) => "touches" in event;

    const getEventPosition = (event) => {
      if (isTouchEvent(event)) {
        const touch = event.touches[0];
        return { x: touch.clientX, y: touch.clientY };
      } else {
        return { x: event.clientX, y: event.clientY };
      }
    };

    const handleMove = (event) => {
      // Si es mouse, solo si está presionado
      if (!isTouchEvent(event) && !lastTouch.current.isMouseDown) return;
      const { x: lastX, y: lastY } = lastTouch.current;
      const { x, y } = getEventPosition(event);
      if (lastX !== null && lastY !== null) {
        const deltaX = x - lastX;
        const deltaY = y - lastY;
        if (ballRef.current && ballRef.current.rotation) {
          ballRef.current.rotation.x += deltaY * 0.01;
          ballRef.current.rotation.y += deltaX * 0.01;
        }
      }
      lastTouch.current = { ...lastTouch.current, x, y };
    };

    const handleStart = (event) => {
      const { x, y } = getEventPosition(event);
      if (isTouchEvent(event)) {
        lastTouch.current = { x, y };
      } else {
        lastTouch.current = { x, y, isMouseDown: true };
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleEnd);
      }
    };

    const handleEnd = (event) => {
      if (isTouchEvent(event)) {
        lastTouch.current = { x: null, y: null };
      } else {
        lastTouch.current = { x: null, y: null, isMouseDown: false };
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", handleEnd);
      }
    };

    const playerPapOffsetConverted = {
      vertical: parseInchFraction(playerPapOffset?.vertical, spanMeasurement),
      horizontal: parseInchFraction(
        playerPapOffset?.horizontal,
        spanMeasurement,
      ),
      direction: playerPapOffset?.direction,
    };
    const pinAboveLineConverted =
      pinAboveLine !== undefined && pinAboveLine !== null
        ? parseInchFraction(pinAboveLine, spanMeasurement)
        : null;
    const showWarning = !isValidSpanMeasurements(spanMeasurements);
    const spanMeasurementsConverted = sanitizeSpanMeasurements(
      spanMeasurements,
      spanMeasurement,
    );

    // DEBUG: Log layoutType
    console.log(
      "[BallGLView] layoutType:",
      layoutType,
      "typeof:",
      typeof layoutType,
    );
    console.log(
      "[BallGLView] playerPapOffsetConverted:",
      playerPapOffsetConverted,
    );

    return (
      <BallView
        $isPhone={isPhone}
        onMouseDown={handleStart}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
        style={{
          touchAction: "none",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {isTextureLoading && (
          <LoadingOverlay>
            <Spinner />
            <span>{t("message.loading_texture", "Loading texture...")}</span>
          </LoadingOverlay>
        )}
        {(hasTextureError || hasLayoutError) && (
          <ErrorOverlay>
            {hasTextureError && (
              <ErrorMessage>
                {t(
                  "message.error.texture_load_failed",
                  "Could not load ball texture",
                )}
              </ErrorMessage>
            )}
            {hasLayoutError && (
              <ErrorMessage>
                {t(
                  "message.error.layout_invalid",
                  "Layout data incomplete - showing basic view",
                )}
              </ErrorMessage>
            )}
          </ErrorOverlay>
        )}
        {/* {showWarning && (
          <div
            style={{
              background: "#ffffff",
              color: "#00147B",
              padding: "8px 12px",
              borderRadius: "12px",
              fontSize: "14px",
              textC: 500,
              border: "1px solid",
              marginBottom: "8px",
              textAlign: "center",
            }}
          >
            {t("message.warning.3d_spec_sheet_not_valid")}
          </div>
        )} */}

        <Canvas camera={{ position: [0, 0, 10], fov: 53 }}>
          <Suspense fallback={null}>
            <ambientLight intensity={0.2} />
            <directionalLight
              position={[-1, 1, 1]}
              intensity={1}
              color={0xffffff}
            />

            {layoutType === "vls" ? (
              <BowlingLayoutVLS
                ref={ballRef}
                pinToPapDistance={parseInchFraction(
                  pinToPapDistance,
                  spanMeasurement,
                )}
                mbToPapDistance={parseInchFraction(
                  mbToPapDistance,
                  spanMeasurement,
                )}
                pinBufferDistance={parseInchFraction(
                  pinBuffer,
                  spanMeasurement,
                )}
                playerPapOffset={playerPapOffsetConverted}
                spanMeasurements={spanMeasurementsConverted}
                spanMeasurement={spanMeasurement}
                cameraPosition={[0, 0, 10]}
                ballId={ballId}
                access_token={access_token}
                onTextureStatusChange={handleTextureStatusChange}
                onLayoutError={handleLayoutError}
              />
            ) : layoutType === "dual_angle" ? (
              <BowlingLayoutDualAngle
                ref={ballRef}
                pinToPap={parseInchFraction(pinToPapDistance, spanMeasurement)}
                pinToPapAngle={Number(pinAngle)}
                valAngleDeg={Number(valAngle)}
                playerPapOffset={playerPapOffsetConverted}
                spanMeasurements={spanMeasurementsConverted}
                spanMeasurement={spanMeasurement}
                cameraPosition={[0, 0, 10]}
                ballId={ballId}
                access_token={access_token}
                onTextureStatusChange={handleTextureStatusChange}
                onLayoutError={handleLayoutError}
              />
            ) : layoutType === "2ls" ? (
              <BowlingLayout2LS
                ref={ballRef}
                pinToPapDistance={parseInchFraction(
                  pinToPapDistance,
                  spanMeasurement,
                )}
                mbToPapDistance={parseInchFraction(
                  mbToPapDistance,
                  spanMeasurement,
                )}
                pinToCenter={parseInchFraction(pinToCenter, spanMeasurement)}
                playerPapOffset={playerPapOffsetConverted}
                spanMeasurements={spanMeasurementsConverted}
                spanMeasurement={spanMeasurement}
                cameraPosition={[0, 0, 10]}
                ballId={ballId}
                access_token={access_token}
                onTextureStatusChange={handleTextureStatusChange}
                onLayoutError={handleLayoutError}
              />
            ) : layoutType === "pal" ? (
              <BowlingLayoutPAL
                ref={ballRef}
                pinToPapDistance={parseInchFraction(
                  pinToPapDistance,
                  spanMeasurement,
                )}
                mbToPapDistance={parseInchFraction(
                  mbToPapDistance,
                  spanMeasurement,
                )}
                pinToCenter={
                  pinAboveLineConverted !== null
                    ? pinAboveLineConverted
                    : parseInchFraction(pinToCenter, spanMeasurement)
                }
                playerPapOffset={playerPapOffsetConverted}
                spanMeasurements={spanMeasurementsConverted}
                spanMeasurement={spanMeasurement}
                cameraPosition={[0, 0, 10]}
                ballId={ballId}
                access_token={access_token}
                onTextureStatusChange={handleTextureStatusChange}
                onLayoutError={handleLayoutError}
              />
            ) : (
              <mesh>
                <sphereGeometry args={[4.294, 64, 64]} />
                <meshStandardMaterial color={0x333333} />
              </mesh>
            )}
          </Suspense>
        </Canvas>
      </BallView>
    );
  },
);

export default BallGLView;
